import React, { useMemo, useEffect, useState } from 'react';

import { DisclaimerModal } from '@vw-marketing/us-components';

import { Disclaimer } from '../../types/main';
import { DisclaimerKeys, getDisclaimersByPage } from '../../utils/disclaimer';
import { fetchScene7Image } from '../../fetch-data/fetch-bg-image';
import { ModelConfig } from '../../types/model';
import { TabItem, TabsData } from '../tabs-data';
import { useDisclaimerConfiguration } from '../../hooks/use-disclaimer-configuration';
import { useFeatureAppContent } from '../../hooks/use-feature-app-content';
import { useModelsConfig } from '../../context/models-config-context';
import { useSharedConfigs } from '../../context/shared-configs-context';
import { VehiclesGallery } from '../vehicles-gallery';
import ModalDisclaimerWrapper from '../modal-disclaimer-wrapper';

const transformDataWithBgImage = async (
  data: { carlineId: string; description: string, backgroundImage: string }[] | undefined,
  carlineIdsMap: { [key: string]: ModelConfig },
  scene7extension?: string,
): Promise<ModelConfig[]> => {
  if (!data) return [];

  const transformedData = await Promise.all(
    data.map(async (vehicle) => {
      const modelConfig = carlineIdsMap[vehicle.carlineId];

      if (!modelConfig) {
        return null;
      }

      let processedBackgroundImage = vehicle.backgroundImage;

      if (vehicle.backgroundImage) {
        try {
          processedBackgroundImage = await fetchScene7Image(
            vehicle.backgroundImage,
            scene7extension
          );
        } catch (error) {
          console.warn(`Failed to fetch background image for ${vehicle.carlineId}:`, error);
          processedBackgroundImage = '';
        }
      }

      return {
        ...modelConfig,
        description: vehicle.description,
        backgroundImage: processedBackgroundImage,
      };
    })
  );

  // @ts-ignore
  return transformedData.filter((item): item is ModelConfig => item !== null);
};

export const CarouselCars: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [processedData, setProcessedData] = useState<{ [key: string]: ModelConfig[] }>({
    sedans: [],
    suvs: [],
    compacts: [],
    electric_vehicles: [],
  });
  
  const { disclaimers } = useDisclaimerConfiguration();
  const { modelOrder } = useSharedConfigs();
  const { sedans, suvs, compacts, electric_vehicles, categoryOrder } = useFeatureAppContent();
  const models = useModelsConfig();

  const carlineIdsMap = useMemo(() => {
    return models.reduce<{ [key: string]: ModelConfig }>(
      (acc, cur) => {
        cur.models.forEach((model) => {
          const modelFromModelOrder = modelOrder.find(
            (v) => v.carlineId === model.carlineId,
          );
          const viewInventoryUrl =
            modelFromModelOrder?.modelCustomArrayCta &&
            modelFromModelOrder.modelCustomArrayCta.filter(
              (cta) => cta.ctaLabel === 'View Inventory',
            );
            
          const inventoryUrl =
            viewInventoryUrl && (viewInventoryUrl[0]?.url && viewInventoryUrl[0]?.urlGsl)
              ? viewInventoryUrl[0].urlGsl
              : undefined;
          acc[model.carlineId] = inventoryUrl
            ? { ...model, inventoryUrl }
            : { ...model };
        });
        return acc;
      },
      {},
    );
  }, [models, modelOrder]);

  const processData = async () => {
    if (!carlineIdsMap || Object.keys(carlineIdsMap).length === 0) {
      return;
    }

    setIsLoading(true);

    try {
      const [processedSedans, processedSuvs, processedCompacts, processedElectricVehicles] =
        await Promise.all([
          transformDataWithBgImage(sedans, carlineIdsMap),
          transformDataWithBgImage(suvs, carlineIdsMap),
          transformDataWithBgImage(compacts, carlineIdsMap),
          transformDataWithBgImage(electric_vehicles, carlineIdsMap),
        ]);

      setProcessedData({
        sedans: processedSedans,
        suvs: processedSuvs,
        compacts: processedCompacts,
        electric_vehicles: processedElectricVehicles,
      });
    } catch (error) {
      console.error('Error processing car data:', error);
      setProcessedData({
        sedans: [],
        suvs: [],
        compacts: [],
        electric_vehicles: [],
      });
    }
  };

  useEffect(() => {
    processData().then(() => setIsLoading(false));
  }, []);

  const tabs: TabItem[] = useMemo(() => {
    const allTabs = [
      {
        key: 'suvs',
        content: <VehiclesGallery vehicles={processedData.suvs} />,
        labelKey: 'carousel.tabs.title.suvs',
      },
      {
        key: 'sedans',
        content: <VehiclesGallery vehicles={processedData.sedans} />,
        labelKey: 'carousel.tabs.title.sedans',
      },
      {
        key: 'compacts',
        content: <VehiclesGallery vehicles={processedData.compacts} />,
        labelKey: 'carousel.tabs.title.compacts',
      },
      {
        key: 'electric_vehicles',
        content: <VehiclesGallery vehicles={processedData.electric_vehicles} />,
        labelKey: 'carousel.tabs.title.evs',
      },
    ];

    if (!categoryOrder || categoryOrder.length === 0) {
      return allTabs;
    }

    return categoryOrder
      .map(categoryKey => allTabs.find(tab => tab.key === categoryKey))
      .filter((tab): tab is TabItem => tab !== undefined);

  }, [processedData, categoryOrder]);

  const landingDisclaimers = getDisclaimersByPage(
    DisclaimerKeys.LandingKey,
    disclaimers,
  );

  return !isLoading && (
    <>
      <TabsData tabs={tabs} />
      <ModalDisclaimerWrapper>
        <DisclaimerModal disclaimers={landingDisclaimers as Disclaimer[]} />
      </ModalDisclaimerWrapper>
    </>
  );
};